import { computed } from 'mobx';
import { PartialDeep } from 'type-fest';

import { translate } from 'services/i18nService';
import { AbstractTheme, CampaignFlagsMap, ControlledFeaturesMap, Theme } from 'themes/themes.types';
import { getThemePart } from 'themes/utils';

import diceIcon from './images/games/dice-icon-xmas.png';
import kenoIcon from './images/games/keno-icon-xmas.png';
import wheelIcon from './images/games/wheel-icon-xmas.png';
import logoImage from './images/logo-xmas.gif';
import tippingTier1Sound from './sounds/tipping/tier-1-xxxmas.mp3';
import tippingTier2Sound from './sounds/tipping/tier-2-xxxmas.mp3';
import tippingTier3Sound from './sounds/tipping/tier-3-xxxmas.mp3';
import tippingTier4Sound from './sounds/tipping/tier-4-xxxmas.mp3';
import styles from './XmasTheme.css';


const SLOGAN_TRANSLATE_KEY = 'campaigns.xxxmas2020.global.title';
const TOKEN_OFFER_TRANSLATE_KEY = 'campaigns.easter2020.mobilePop.body';

export default class XmasTheme extends AbstractTheme {
  static CFS: ControlledFeaturesMap = {};

  static CAMPAIGN_FLAGS: CampaignFlagsMap = {
    GAMES: 'XMAS_GAMES',
    LOGO: 'XMAS_LOGO',
    TIPPING: 'XMAS_TIPPING',
    ON_CAM_ANIMATION: 'XMAS_ON_CAM_ANIMATION',
    BUY_TOKENS_MODAL: 'XMAS_BUY_TOKENS_MODAL',
    HEADER_CTA: 'XMAS_HEADER_CTA',
    MOBILE_OVERLAY: 'XMAS_MOBILE_OVERLAY',
    CHAT_OFFER: 'XMAS_CHAT_OFFER',
    INFO_STRIP: 'XMAS_INFO_STRIP',
  };

  @computed get theme(): PartialDeep<Theme> {
    return {
      games: getThemePart<'games', true>(XmasTheme.CAMPAIGN_FLAGS.GAMES, () => {
        return {
          keno: {
            icon: kenoIcon,
            borderClass: styles.kenoBorder,
            containerClass: styles.kenoContainer,
          },
          wheel: {
            icon: wheelIcon,
            containerClass: styles.wheelContainer,
            outerCircleClass: styles.wheelOuterCircle,
            arrowClass: styles.wheelArrow,
            centerClass: styles.wheelCenter,
          },
          dice: {
            icon: diceIcon,
            containerClass: styles.diceContainer,
          },
        };
      }),

      logoImage: getThemePart<'logoImage'>(XmasTheme.CAMPAIGN_FLAGS.LOGO, () => logoImage),

      tipping: getThemePart<'tipping', true>(XmasTheme.CAMPAIGN_FLAGS.TIPPING, () => {
        return {
          tier1Sound: tippingTier1Sound,
          tier2Sound: tippingTier2Sound,
          tier3Sound: tippingTier3Sound,
          tier4Sound: tippingTier4Sound,
        };
      }),

      onCamAnimation: getThemePart<'onCamAnimation'>(XmasTheme.CAMPAIGN_FLAGS.ON_CAM_ANIMATION, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          animBackgroundClass: styles.animBackground,
          animBackground2Class: styles.animBackground2,
          staticBackgroundClass: styles.staticBackground,
          animWrapperClass: styles.animWrapper,
          line1Text: translate(TOKEN_OFFER_TRANSLATE_KEY,
            { 0: campaign.campaignDetails.bonusTokenPercent }
          ),
        };
      }),

      buyTokensModal: getThemePart<'buyTokensModal'>(XmasTheme.CAMPAIGN_FLAGS.BUY_TOKENS_MODAL, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          titleClass: styles.tokenOfferTitle,
          headerClass: styles.buyTokensModalHeader,
          title: translate(SLOGAN_TRANSLATE_KEY),
          text: translate(TOKEN_OFFER_TRANSLATE_KEY,
            { 0: campaign.campaignDetails.bonusTokenPercent }
          ),
        };
      }),

      headerCTA: getThemePart<'headerCTA'>(XmasTheme.CAMPAIGN_FLAGS.HEADER_CTA, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          wrapperClass: styles.headerCTAWrapper,
          title: translate(SLOGAN_TRANSLATE_KEY),
        };
      }),

      mobileOverlay: getThemePart<'mobileOverlay'>(XmasTheme.CAMPAIGN_FLAGS.MOBILE_OVERLAY, (campaign) => {
        return {
          cookieNamePrefix: campaign.activeCampaignName,
          title: translate(SLOGAN_TRANSLATE_KEY),
          text: translate(TOKEN_OFFER_TRANSLATE_KEY,
            { 0: campaign.campaignDetails.bonusTokenPercent }
          ),
          titleClass: styles.tokenOfferTitle,
          bodyClass: styles.mobileOverlayBody,
        };
      }),

      chatOffer: getThemePart<'chatOffer'>(XmasTheme.CAMPAIGN_FLAGS.CHAT_OFFER, (campaign) => {
        return {
          titleClass: styles.tokenOfferTitle,
          wrapperClass: styles.chatOfferWrapperClass,
          title: translate(SLOGAN_TRANSLATE_KEY),
          text: translate(TOKEN_OFFER_TRANSLATE_KEY,
            { 0: campaign.campaignDetails.bonusTokenPercent }
          ),
        };
      }),

      infoStrip: getThemePart<'infoStrip'>(XmasTheme.CAMPAIGN_FLAGS.INFO_STRIP, (campaign) => {
        return {
          campaignSecondsLeft: campaign.campaignDetails.timeLeft,
          titleClass: styles.tokenOfferTitle,
          wrapperClass: styles.infoStripWrapperClass,
          title: translate(SLOGAN_TRANSLATE_KEY),
          text: translate(TOKEN_OFFER_TRANSLATE_KEY,
            { 0: campaign.campaignDetails.bonusTokenPercent }
          ),
          useTimer: true,
        };
      }),

    };
  }
}
